import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import SeoHead from "../components/SeoHead";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Insights() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/posts", { params: { category, search, page, limit: 9 } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [category, search, page]);

  useEffect(() => {
    api.get("/posts/categories").then((r) => setCategories(["All", ...r.data.categories]));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const featured = page === 1 && !search && category === "All" ? data.items[0] : null;
  const rest = featured ? data.items.slice(1) : data.items;

  return (
    <div data-testid="insights-page" className="pt-[72px]">
      <SeoHead
        title="Insights | Local Proof STL — Executive Operations Thinking"
        description="Executive thinking from Local Proof STL on operational excellence, Lean, leadership, AI, and continuous improvement for owner-led service businesses."
        canonical="https://localproofstl.com/insights"
        ogType="website"
        ogUrl="https://localproofstl.com/insights"
        ogImage="https://localproofstl.com/og-image.jpg"
        twitterImage="https://localproofstl.com/og-image.jpg"
      />
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-12">
        <Reveal>
          <p className="eyebrow mb-8">Insights</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
            Thinking on operational excellence.
          </h1>
          <p className="mt-8 max-w-xl text-lg text-charcoal/80 leading-relaxed">
            Perspectives on leadership, Lean, AI, continuous improvement, and scaling service businesses.
          </p>
        </Reveal>
      </section>

      {/* Controls */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-border pb-6">
          <div className="flex flex-wrap gap-2" data-testid="insights-categories">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`category-${c.toLowerCase()}`}
                onClick={() => { setCategory(c); setPage(1); }}
                className={`text-sm px-4 py-2 rounded-full border transition-colors duration-200 ${
                  category === c ? "bg-ink text-white border-ink" : "border-border text-steel hover:border-ink/40 hover:text-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-steel" />
            <input
              data-testid="insights-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search articles"
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-border rounded-full focus:outline-none focus:border-ink transition-colors"
            />
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32">
        {loading ? (
          <p className="text-steel py-20 text-center">Loading articles…</p>
        ) : data.items.length === 0 ? (
          <p data-testid="insights-empty" className="text-steel py-20 text-center">No articles found.</p>
        ) : (
          <>
            {featured && (
              <Reveal>
                <Link
                  to={`/insights/${featured.slug}`}
                  data-testid="insights-featured"
                  className="group grid lg:grid-cols-2 gap-8 mb-16 bg-white border border-border rounded-2xl overflow-hidden hover:shadow-[0_30px_70px_-40px_rgba(11,31,51,0.35)] transition-shadow duration-300"
                >
                  <div className="relative h-64 lg:h-full min-h-[280px] overflow-hidden">
                    <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3 text-xs text-steel mb-4">
                      <span className="uppercase tracking-widest font-semibold">{featured.category}</span>
                      <span>·</span><span>{featured.reading_time} min read</span>
                    </div>
                    <h2 className="font-serif text-3xl lg:text-4xl text-ink leading-tight mb-4">{featured.title}</h2>
                    <p className="text-charcoal/70 leading-relaxed mb-6">{featured.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-ink text-sm font-semibold">
                      Read article <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((post, i) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={`/insights/${post.slug}`}
                    data-testid={`insight-card-${post.slug}`}
                    className="group block h-full"
                  >
                    <div className="relative h-52 rounded-xl overflow-hidden mb-5 border border-border">
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-steel mb-3">
                      <span className="uppercase tracking-widest font-semibold">{post.category}</span>
                      <span>·</span><span>{fmtDate(post.published_at)}</span>
                    </div>
                    <h3 className="font-serif text-2xl text-ink leading-snug mb-2 group-hover:text-steel transition-colors">{post.title}</h3>
                    <p className="text-charcoal/70 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                  </Link>
                </motion.div>
              ))}
            </div>

            {data.pages > 1 && (
              <div className="flex justify-center gap-2 mt-16" data-testid="insights-pagination">
                {Array.from({ length: data.pages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-full text-sm border transition-colors ${
                      page === i + 1 ? "bg-ink text-white border-ink" : "border-border text-steel hover:border-ink"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
